import { useEffect, useRef } from "react";
import L from "leaflet";
import { useLeafletMap, useMapClick } from "@/lib/useLeafletMap";
import { onCursor } from "@/lib/cursor";
import { posAt } from "@/lib/telemetry";

export const SatelliteMap = ({ center, points, colors, tips, cursors, axis, marks, picking, onPick, h = 460 }) => {
  const [ref, map] = useLeafletMap(center, 17);
  const layer = useRef(null);
  const fitted = useRef(false);
  useMapClick(map, picking, onPick);

  useEffect(() => {
    if (!map) {
      return;
    }
    layer.current?.remove();
    const g = L.layerGroup();
    for (let i = 1; i < points.length; i++) {
      L.polyline([points[i - 1], points[i]], { color: colors[i - 1], weight: 4, opacity: 0.95 })
        .bindTooltip(tips[i - 1], { sticky: true, className: "trace-tip" }).addTo(g);
    }
    L.circleMarker(points[0], { radius: 5, color: "#090A0F", weight: 2, fillColor: "#fff", fillOpacity: 1 }).addTo(g);
    marks.forEach((m) => {
      const p = posAt(cursors[0].lap, points, m.t, "t");
      if (p) {
        L.circleMarker(p, { radius: 4, color: "#090A0F", weight: 1, fillColor: "#FFB800", fillOpacity: 1 })
          .bindTooltip(m.label, { permanent: true, direction: "right", className: "trace-tip" }).addTo(g);
      }
    });
    g.addTo(map);
    layer.current = g;
    if (!fitted.current) {
      map.fitBounds(L.latLngBounds(points), { padding: [24, 24] });
      fitted.current = true;
    }
  }, [map, points, colors, tips, marks, cursors]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }
    const markers = cursors.map((c) => L.circleMarker(c.points[0], { radius: 8, color: "#fff", weight: 2, fillColor: c.color, fillOpacity: 1, interactive: false }));
    const off = onCursor((v) => {
      cursors.forEach((c, k) => {
        const p = posAt(c.lap, c.points, v, axis);
        if (!p) {
          markers[k].remove();
          return;
        }
        markers[k].setLatLng(p);
        if (!map.hasLayer(markers[k])) markers[k].addTo(map);
      });
    });
    return () => {
      off();
      markers.forEach((m) => m.remove());
    };
  }, [map, cursors, axis]);

  useEffect(() => {
    if (map) {
      map.invalidateSize();
    }
  }, [map, h]);

  return <div className="relative z-0"><div ref={ref} data-testid="telemetry-satellite-map" style={{ height: h }} className="w-full rounded-sm" /></div>;
};
